import { Image, Text } from '@react-pdf/renderer';
import { PropsWithChildren, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { ModalDialog } from 'components/modal/Modal';
import { useModal } from 'components/modal/ModalContext';
import { IFile } from 'features/files/fileAPI';
import {
  getFileTypeInfo,
  isImageType,
  isVideoType,
} from 'features/files/fileSlice';
import { getLibraryPathForFile } from './LibraryFileViewer';

type ImageViewLinkProps = {
  file: IFile;
  noPreview?: boolean;
  className?: string;
};

export function ImageViewLink(props: ImageViewLinkProps & PropsWithChildren) {
  const { file, noPreview = false, className, children } = props;
  const intl = useIntl();
  const { openModal } = useModal();

  const handleImageClick = useCallback(
    (e: React.MouseEvent) => {
      // rendering image
      e.preventDefault();
      openModal(
        <ModalDialog
          title={file.description}
          height="max"
          width="max"
          bare
          enableEscape
          noFocusOnClose
        >
          <div className="img_view">
            <img src={file.uri} alt={file.description} />
          </div>
        </ModalDialog>,
      );
    },
    [openModal, file],
  );

  const handleVideoClick = useCallback(
    (e: React.MouseEvent) => {
      // rendering video
      e.preventDefault();
      openModal(
        <ModalDialog
          title={file.description}
          height="max"
          width="max"
          bare
          enableEscape
          noFocusOnClose
        >
          <div className="vid_view">
            <video
              id="my-video"
              autoPlay
              controls
              preload="auto"
              width="auto"
              height="auto"
              aria-label={file.description}
            >
              <source src={file.uri} type="video/mp4" />
            </video>
          </div>
        </ModalDialog>,
      );
    },
    [openModal, file],
  );

  const handleLinkClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      // since we've disabled default clicks on mouse down,
      // we need to open the link manually
      // window.open(file.uri, '_blank', 'noopener,noreferrer');
      const uri = getLibraryPathForFile(file);
      window.open(uri, '_blank', 'noopener,noreferrer');
    },
    [file],
  );

  const ftInfo = getFileTypeInfo(file);
  if (isImageType(ftInfo.mime))
    // file is an image
    return (
      <div
        className={className ?? 'msg_file'}
        tabIndex={0}
        onMouseDown={(e) => e.preventDefault()}
        onClick={handleImageClick}
      >
        {children ? (
          children
        ) : (
          <img src={`${file.uri}`} alt={file.description} />
        )}
      </div>
    );
  if (isVideoType(ftInfo.mime))
    // file is a video
    return (
      <div
        className={className ?? 'msg_file'}
        tabIndex={0}
        onMouseDown={(e) => e.preventDefault()}
        onClick={handleVideoClick}
      >
        {children ? (
          children
        ) : (
          <img src={`${file.uri}`} alt={file.description} />
        )}
      </div>
    );
  else
    // we have a link
    return (
      <Link
        to={getLibraryPathForFile(file)}
        target="_blank"
        reloadDocument
        onMouseDown={(e) => e.preventDefault()}
        onClick={handleLinkClick}
      >
        {children}
      </Link>
    );
}

export function ImageViewLinkToPdf(
  props: ImageViewLinkProps & PropsWithChildren,
) {
  const { file, noPreview = false, className, children } = props;
  const ftInfo = getFileTypeInfo(file);
  if (isImageType(ftInfo.mime)) return <Image src={file.uri} />;
  else return <Text>{file.uri ?? ''}</Text>;
}
